import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { trpc } from "@/lib/trpc";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { CheckCircle, Loader2, ListOrdered, Calendar, Clock } from "lucide-react";
import { useFeatures } from "@/components/FeatureGuard";
import { useAuth } from "@/_core/hooks/useAuth";

interface SessionBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: number;
  sessionDate: string;
  courseName: string;
  courseTime: string;
  onSuccess?: () => void;
}

export function SessionBookingDialog({
  open,
  onOpenChange,
  sessionId,
  sessionDate,
  courseName,
  courseTime,
  onSuccess,
}: SessionBookingDialogProps) {
  const { user } = useAuth();
  const { features } = useFeatures();
  const utils = trpc.useUtils();

  // Get session availability
  const { data: availability } = trpc.sessionBooking.getAvailability.useQuery(
    { sessionId },
    { enabled: open && sessionId > 0 }
  );

  const [joinWaitlist, setJoinWaitlist] = useState(false);
  const [formData, setFormData] = useState({
    participantName: "",
    participantEmail: "",
    participantPhone: "",
    notes: "",
    smsConsent: false,
  });

  // Pre-fill form with user data when available
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        participantName: user.name || prev.participantName,
        participantEmail: user.email || prev.participantEmail,
      }));
    }
  }, [user]);

  const bookingMutation = trpc.sessionBooking.create.useMutation({
    onSuccess: (result) => {
      if (result.status === "waitlist") {
        toast.success("Auf Warteliste eingetragen!", {
          description: `Sie sind auf Position ${result.waitlistPosition} der Warteliste.`,
        });
      } else {
        toast.success("Anmeldung erfolgreich!", {
          description: "Sie erhalten in Kürze eine Bestätigung per E-Mail.",
        });
      }
      onOpenChange(false);
      resetForm();
      utils.sessionBooking.getAvailability.invalidate({ sessionId });
      onSuccess?.();
    },
    onError: (error) => {
      toast.error("Anmeldung fehlgeschlagen", {
        description: error.message,
      });
    },
  });

  // Quick book mutation for logged-in users
  const quickBookMutation = trpc.sessionBooking.quickBook.useMutation({
    onSuccess: (result) => {
      if (result.status === "waitlist") {
        toast.success("Auf Warteliste eingetragen!", {
          description: `Sie sind auf Position ${result.waitlistPosition} der Warteliste.`,
        });
      } else {
        toast.success("Anmeldung erfolgreich!");
      }
      onOpenChange(false);
      utils.sessionBooking.getAvailability.invalidate({ sessionId });
      onSuccess?.();
    },
    onError: (error) => {
      toast.error("Anmeldung fehlgeschlagen", {
        description: error.message,
      });
    },
  });

  const resetForm = () => {
    setFormData({
      participantName: user?.name || "",
      participantEmail: user?.email || "",
      participantPhone: "",
      notes: "",
      smsConsent: false,
    });
    setJoinWaitlist(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    bookingMutation.mutate({
      sessionId,
      ...formData,
      joinWaitlist: isFull ? joinWaitlist : false,
    });
  };

  const handleQuickBook = () => {
    quickBookMutation.mutate({
      sessionId,
      joinWaitlist: isFull ? joinWaitlist : false,
    });
  };

  const isFull = availability?.isFull ?? false;
  const waitlistEnabled = features.waitlistEnabled && (availability?.waitlistEnabled ?? false);
  const isCancelled = availability?.isCancelled ?? false;

  // Format date for display
  const formattedDate = new Date(sessionDate).toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  if (isCancelled) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Session abgesagt</DialogTitle>
            <DialogDescription>
              Diese Session wurde leider abgesagt.
            </DialogDescription>
          </DialogHeader>
          <Button onClick={() => onOpenChange(false)}>Schließen</Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isFull && waitlistEnabled ? "Warteliste beitreten" : "Termin buchen"}
          </DialogTitle>
          <DialogDescription>
            {courseName}
          </DialogDescription>
        </DialogHeader>

        {/* Session Info */}
        <div className="rounded-lg bg-muted/50 p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-primary" />
            <span className="font-medium">{formattedDate}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>{courseTime}</span>
          </div>
          {availability && (
            <div className="text-sm text-muted-foreground">
              {isFull ? (
                <span className="text-orange-600">Ausgebucht</span>
              ) : (
                <span>{availability.availableSpots} {availability.availableSpots === 1 ? "Platz" : "Plätze"} frei</span>
              )}
            </div>
          )}
        </div>

        {/* Quick Book for logged-in users */}
        {user && !isFull && (
          <div className="space-y-3">
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              onClick={handleQuickBook}
              disabled={quickBookMutation.isPending}
            >
              {quickBookMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird gebucht...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Jetzt buchen
                </>
              )}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Buchung als {user.name || user.email}
            </p>
          </div>
        )}

        {/* Full booking form for guests or when waitlist */}
        {(!user || isFull) && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                required
                value={formData.participantName}
                onChange={(e) => setFormData(prev => ({ ...prev, participantName: e.target.value }))}
                placeholder="Ihr vollständiger Name"
                readOnly={!!user}
                className={user ? "bg-muted" : ""}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail *</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.participantEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, participantEmail: e.target.value }))}
                placeholder="ihre@email.de"
                readOnly={!!user}
                className={user ? "bg-muted" : ""}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Telefon</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.participantPhone}
                onChange={(e) => setFormData(prev => ({ ...prev, participantPhone: e.target.value }))}
                placeholder="+49 ..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Anmerkungen</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Haben Sie besondere Wünsche oder Hinweise?"
                rows={2}
              />
            </div>

            {/* SMS consent */}
            {features.smsEnabled && formData.participantPhone && (
              <div className="flex items-start space-x-3 rounded-lg border p-3 bg-muted/50">
                <Checkbox
                  id="smsConsent"
                  checked={formData.smsConsent}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, smsConsent: checked === true }))}
                />
                <div className="space-y-1">
                  <Label htmlFor="smsConsent" className="text-sm font-medium cursor-pointer">
                    SMS-Benachrichtigungen
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Über Terminänderungen per SMS informiert werden.
                  </p>
                </div>
              </div>
            )}

            {/* Waitlist checkbox when full */}
            {isFull && waitlistEnabled && (
              <div className="flex items-start space-x-3 rounded-lg border p-3 bg-muted/50">
                <Checkbox
                  id="waitlist"
                  checked={joinWaitlist}
                  onCheckedChange={(checked) => setJoinWaitlist(checked === true)}
                />
                <div className="space-y-1">
                  <Label htmlFor="waitlist" className="text-sm font-medium cursor-pointer">
                    Auf Warteliste setzen
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Benachrichtigung bei freiem Platz.
                  </p>
                </div>
              </div>
            )}

            <Button
              type="submit"
              className={`w-full ${isFull ? "bg-secondary hover:bg-secondary/90" : "bg-primary hover:bg-primary/90"}`}
              disabled={bookingMutation.isPending || (isFull && !joinWaitlist)}
            >
              {bookingMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird gesendet...
                </>
              ) : isFull ? (
                <>
                  <ListOrdered className="mr-2 h-4 w-4" />
                  Auf Warteliste setzen
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Termin buchen
                </>
              )}
            </Button>
          </form>
        )}

        {/* Show sold out message if full and no waitlist */}
        {isFull && !waitlistEnabled && (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground">
              Dieser Termin ist leider ausgebucht.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
